import { getApiBaseUrl } from '../config/apiBaseUrl';
import { getSetupIntentIdFromClientSecret } from '../utils/stripeSetupIntent';
import {
  getPendingSetupClientSecret,
  getPendingSetupIntentId,
  clearPendingSetupIntent,
} from '../utils/pendingSetupIntent';

class BillingApiService {
  async makeRequest(endpoint, options = {}) {
    const url = `${getApiBaseUrl()}${endpoint}`;
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    const contentType = response.headers.get('content-type');
    const data =
      contentType && contentType.includes('application/json')
        ? await response.json()
        : { error: await response.text() };

    if (!response.ok) {
      const error = new Error(data.error || `HTTP error! status: ${response.status}`);
      error.code = data.code;
      throw error;
    }

    return data;
  }

  getConfig() {
    return this.makeRequest('/api/billing/config');
  }

  getPaymentStatus(accessToken) {
    return this.makeRequest('/api/billing/payment-status', {
      method: 'POST',
      body: JSON.stringify({ accessToken }),
    });
  }

  createSetupIntent(accessToken) {
    return this.makeRequest('/api/billing/setup-intent', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  getAccount(accessToken) {
    return this.makeRequest('/api/billing/account', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  getUsageSummary(accessToken) {
    return this.makeRequest('/api/billing/usage-summary', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  async completeSetup(accessToken, setupIntentId, setupIntentClientSecret, options = {}) {
    const secret =
      setupIntentClientSecret || getPendingSetupClientSecret() || '';
    const resolvedId =
      setupIntentId ||
      getSetupIntentIdFromClientSecret(secret) ||
      getPendingSetupIntentId();

    const body = {};
    if (resolvedId) {
      body.setupIntentId = String(resolvedId);
    }
    if (secret) {
      body.setupIntentClientSecret = String(secret);
      body.clientSecret = String(secret);
    }
    if (options.updateOnly) {
      body.updateOnly = true;
    } else {
      // Always send explicit boolean for onboarding (Lambda validates this field).
      body.monthlyBillingAuthorized = options.monthlyBillingAuthorized === true;
    }

    const result = await this.makeRequest('/api/billing/complete-setup', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(body),
    });

    clearPendingSetupIntent();
    return result;
  }
}

const billingApi = new BillingApiService();
export default billingApi;

/**
 * After login, return path for dashboard or payment setup.
 */
export async function getPostLoginPath() {
  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken) {
    return '/provider/login';
  }

  try {
    const status = await billingApi.getPaymentStatus(accessToken);
    if (!status.stripeEnabled) {
      return '/provider/';
    }
    // Always require a saved card when Stripe billing is enabled (ignore legacy
    // deviceFullyRegistered that skipped payment for old DynamoDB rows).
    if (!status.paymentMethodOnFile) {
      return '/provider/payment-setup';
    }
  } catch (err) {
    console.warn('Could not check payment status:', err.message);
  }

  return '/provider/';
}
